import * as React from "react";
import ReactDOM from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useQueryClient, useIsMutating } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Dialog, DialogContent } from "@jhool-io/fe-components";
import { Toaster } from "react-hot-toast";
import PaymentLinkDetails from "../PaymentLinkDetails/PaymentLinkDetails";
import PaymentLinkCodeVerification from "../PaymentLinkCodeVerification/PaymentLinkCodeVerification";
import PaymentLinkPaymentForm from "../PaymentLinkPaymentForm/PaymentLinkPaymentForm";
import { useGetPaymentLinkDetails } from "../../hooks/queries/payment-link.queries";
import {
    IPaymentLinkDetails,
    PaymentLinkType,
} from "../../types/payment-link.types";
import { IApiResponse } from "../../../../utils/types/api-response";
import useToast from "../../../../hooks/useToast";
import { getPaymentLinkDetails } from "../../services/payment-link.services";

import PaymentLinkStatus from "../PaymentLinkStatus/PaymentLinkStatus";

type PaymentLinkDialog =
    | null
    | "code-verification"
    | "payment-form"
    | "success"
    | "expired"
    | "used"
    | "empty";

export default function PaymentLinkWrapper() {
    const [searchParams] = useSearchParams();
    const ref = searchParams.get("ref");
    const linkType = searchParams.get("type") as PaymentLinkType | null;

    const [dialogInView, setDialogInView] =
        React.useState<PaymentLinkDialog>(null);
    const [verificationCode, setVerificationCode] = React.useState<
        string | null
    >(null);

    const { data, isLoading, error } = useGetPaymentLinkDetails(
        ref || "",
        undefined,
        Boolean(ref) && !verificationCode
    );
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const isMutating = useIsMutating(["process-payment-link-payment"]);
    const [isVerifyingCode, setIsVerifyingCode] = React.useState(false);

    const handleDialogInView = (dialog: PaymentLinkDialog) => {
        setDialogInView(dialog);
    };

    const errorMessage = error?.response?.data?.message || null;

    const isUsedError = errorMessage === "Payment link has already been used";
    const isExpiredError = errorMessage === "Payment link has expired";

    const handleCodeSubmit = React.useCallback(
        async (code: string) => {
            if (!ref) return;
            setIsVerifyingCode(true);
            try {
                const response = await queryClient.fetchQuery({
                    queryKey: ["payment-link-details", ref, code],
                    queryFn: () => getPaymentLinkDetails(ref, code),
                });
                setVerificationCode(code);
                if (response.data && !response.data.code_required) {
                    queryClient.setQueryData(
                        ["payment-link-details", ref],
                        response
                    );
                    handleDialogInView(null);
                } else {
                    toast({
                        mode: "error",
                        message: "Invalid verification code. Please try again.",
                    });
                }
            } catch (err) {
                const axiosErr = err as AxiosError<
                    IApiResponse<IPaymentLinkDetails>
                >;
                const message =
                    axiosErr.response?.data?.message ||
                    "Failed to verify code. Please try again.";

                toast({
                    mode: "error",
                    message,
                });
            } finally {
                setIsVerifyingCode(false);
            }
        },
        [ref, queryClient, toast]
    );

    const handleContinueToPayment = () => {
        handleDialogInView("payment-form");
    };

    const handlePaymentFormSubmit = () => {
        handleDialogInView("success");
    };

    const shouldShowCodeVerification =
        !isLoading &&
        data?.data?.code_required &&
        dialogInView !== "payment-form" &&
        dialogInView !== "success" &&
        !isUsedError &&
        !isExpiredError;

    const shouldShowEmpty =
        !isLoading &&
        data &&
        !data.data &&
        dialogInView !== "payment-form" &&
        dialogInView !== "success" &&
        !isUsedError &&
        !isExpiredError;

    const showDetails =
        !isLoading &&
        data?.data &&
        !data.data.code_required &&
        dialogInView !== "code-verification" &&
        dialogInView !== "payment-form" &&
        dialogInView !== "success" &&
        !isUsedError &&
        !isExpiredError &&
        !shouldShowEmpty;

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="page-loader">
                    <span />
                </div>
            </div>
        );
    }

    return (
        <>
            {showDetails && (
                <PaymentLinkDetails
                    paymentLinkData={data?.data as IPaymentLinkDetails}
                    onContinueToPayment={handleContinueToPayment}
                />
            )}

            <Dialog
                open={
                    (Boolean(shouldShowCodeVerification) ||
                        Boolean(dialogInView === "code-verification")) &&
                    Boolean(ref)
                }
            >
                <DialogContent
                    variant="center"
                    title="Enter Verification Code"
                    handleCloseDialog={() => {
                        window.location.href = "/login";
                    }}
                    showFooter
                    saveText="Verify Code"
                    cancelText="Cancel"
                    submitBtnFormValue="payment-link-code-verification"
                    onCancelClick={() => {
                        window.location.href = "/login";
                    }}
                    isSubmitBtnDisabled={isVerifyingCode}
                    isCancelBtnDisabled={isVerifyingCode}
                >
                    <PaymentLinkCodeVerification
                        onFormSubmit={handleCodeSubmit}
                        isLoading={isVerifyingCode}
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={
                    dialogInView === "payment-form" &&
                    Boolean(data?.data) &&
                    Boolean(ref)
                }
            >
                <DialogContent
                    variant="center"
                    title="Card Details"
                    handleCloseDialog={() => {
                        handleDialogInView(null);
                    }}
                    showFooter
                    saveText="Pay Now"
                    cancelText="Cancel"
                    submitBtnFormValue="payment-link-payment-form"
                    isSubmitBtnDisabled={isMutating > 0}
                    isCancelBtnDisabled={isMutating > 0}
                    wrapperClass="w-[600px] max-w-[80vw]"
                >
                    <PaymentLinkPaymentForm
                        paymentLinkRef={ref || ""}
                        totalAmount={data?.data?.total_amount || 0}
                        linkType={linkType}
                        verificationCode={verificationCode}
                        onFormSubmit={handlePaymentFormSubmit}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={dialogInView === "success"}>
                <DialogContent
                    variant="center"
                    title="Payment Successful"
                    handleCloseDialog={() => {
                        window.location.href = "/login";
                    }}
                    showFooter={false}
                    wrapperClass="w-[500px] max-w-[90vw]"
                >
                    <PaymentLinkStatus
                        variant="success"
                        description="Your payment has been processed successfully. A receipt will be sent to your email."
                        buttonText="Done"
                        onButtonClick={() => {
                            window.location.href = "/login";
                        }}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={isExpiredError || dialogInView === "expired"}>
                <DialogContent
                    variant="center"
                    title="Link Expired"
                    handleCloseDialog={() => {
                        window.location.href = "/login";
                    }}
                    showFooter={false}
                    wrapperClass="w-[500px] max-w-[90vw]"
                >
                    <PaymentLinkStatus
                        variant="error"
                        description="This payment link has expired. Please contact billing@humantold.com for a new payment link."
                        buttonText="Go Home"
                        onButtonClick={() => {
                            window.location.href = "/login";
                        }}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={isUsedError || dialogInView === "used"}>
                <DialogContent
                    variant="center"
                    title="Link Already Used"
                    handleCloseDialog={() => {
                        window.location.href = "/login";
                    }}
                    showFooter={false}
                    wrapperClass="w-[500px] max-w-[90vw]"
                >
                    <PaymentLinkStatus
                        variant="error"
                        description="This invoice has already been paid or the link has already been used."
                        buttonText="Go Home"
                        onButtonClick={() => {
                            window.location.href = "/login";
                        }}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={shouldShowEmpty || dialogInView === "empty"}>
                <DialogContent
                    variant="center"
                    title="Payment Link Not Found"
                    handleCloseDialog={() => {
                        window.location.href = "/login";
                    }}
                    showFooter={false}
                    wrapperClass="w-[500px] max-w-[90vw]"
                >
                    <PaymentLinkStatus
                        variant="error"
                        description="We couldn't find the payment link you're looking for. It may have been deleted or the link is invalid."
                        buttonText="Go Home"
                        onButtonClick={() => {
                            window.location.href = "/login";
                        }}
                    />
                </DialogContent>
            </Dialog>
            {ReactDOM.createPortal(
                <Toaster
                    containerStyle={{ zIndex: 99999 }}
                    toastOptions={{
                        style: { zIndex: 99999 },
                    }}
                />,
                document.body
            )}
        </>
    );
}
